import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import { z } from "zod";
import { useIsFeatureFlagEnabled } from "../../featureFlags/useIsFeatureFlagEnabled";
import { queryClient } from "../../queryClientProvider";
import { deliveryRouteSequenceStatusSchema } from "../../types/planning/plan.type";
import { http } from "../../utils/httpCommon";
import { safeParsePartial } from "../../utils/zodSafeParse";
import { useApiUrls } from "../useApiUrl";

const deliveryRouteSequenceStatusResponseSchema = z.array(
  deliveryRouteSequenceStatusSchema
);

export type DeliveryRouteSequenceStatus = z.infer<
  typeof deliveryRouteSequenceStatusSchema
>;

const checkDeliveryRouteSequenceStatusRefetchInterval = 1000; // 1s

type UseDeliveryRoutesSequenceStatusProps = {
  routeIds: number[];
  timestamp: number;
  onAutoSequenceRoutesFinished: (result: DeliveryRouteSequenceStatus[]) => void;
};

const keys = {
  getDeliveryRoutesSequenceStatus: (
    routeIds: number[],
    processId: number,
    _: number
  ) => ["delivery-routes", "sequence-status", routeIds, processId],
};

export const useDeliveryRoutesSequenceStatus = ({
  onAutoSequenceRoutesFinished,
  timestamp: processId,
  routeIds,
}: UseDeliveryRoutesSequenceStatusProps) => {
  const countRef = useRef(0);
  const lastProcessedSignatureRef = useRef<string | null>(null);
  const onFinishedRef = useRef(onAutoSequenceRoutesFinished);
  onFinishedRef.current = onAutoSequenceRoutesFinished;

  // Track if we've seen "processing" status at least once for this processId
  const hasSeenProcessingRef = useRef<Record<number, boolean>>({});
  const routeIdsSignature = useMemo(() => routeIds.join(","), [routeIds]);
  useEffect(() => {
    countRef.current = 0;
    hasSeenProcessingRef.current = {};
    lastProcessedSignatureRef.current = null;
  }, [processId, routeIdsSignature]);

  const hookUrls = useApiUrls();
  const { getDeliveryRoutesSequenceStatus } = hookUrls;
  const isSafeParseEnabled = useIsFeatureFlagEnabled(
    "operations-5864-inbound-zod-parser-client"
  );

  const { data: sequenceStatus = [] } = useQuery({
    enabled: routeIds.length > 0,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: keys.getDeliveryRoutesSequenceStatus(
      routeIds,
      processId,
      countRef.current
    ),
    queryFn: async () => {
      const { data } = await http.get(
        getDeliveryRoutesSequenceStatus(routeIds)
      );
      countRef.current += 1;
      return isSafeParseEnabled
        ? safeParsePartial(
            deliveryRouteSequenceStatusResponseSchema,
            data,
            "useDeliveryRoutesSequenceStatus"
          )
        : deliveryRouteSequenceStatusResponseSchema.parse(data);
    },
    refetchInterval: () =>
      countRef.current < 150
        ? checkDeliveryRouteSequenceStatusRefetchInterval
        : false,
  });

  // Check if any route is currently "processing"
  const hasProcessingStatus = sequenceStatus.some(
    (route) => route.sequenceStatus === "processing"
  );

  // If we see "processing", mark it for this processId
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (hasProcessingStatus && processId !== null && processId !== undefined) {
    hasSeenProcessingRef.current[processId] = true;
  }

  // Only consider finished if:
  // 1. We have sequence status data
  // 2. No routes are "processing"
  // 3. We've seen "processing" at least once (to avoid finishing on stale "processed" from previous run)
  const isAutoSequenceFinished =
    sequenceStatus.length > 0 &&
    sequenceStatus.every((route) => route.sequenceStatus !== "processing") &&
    hasSeenProcessingRef.current[processId] === true;

  const routeIdsKey = routeIds.join(",");
  const sequenceSignature = useMemo(
    () => sequenceStatus.map((s) => `${s.id}:${s.sequenceStatus}`).join("|"),
    [sequenceStatus]
  );

  useEffect(() => {
    if (!isAutoSequenceFinished || routeIds.length === 0) return;

    const signature = [String(processId), routeIdsKey, sequenceSignature].join(
      "::"
    );

    if (lastProcessedSignatureRef.current === signature) return;
    lastProcessedSignatureRef.current = signature;

    queryClient.setQueryData(
      keys.getDeliveryRoutesSequenceStatus(
        routeIds,
        processId,
        countRef.current
      ),
      []
    );

    onFinishedRef.current(sequenceStatus);

    sequenceStatus.forEach((sequencedRoute) => {
      if (sequencedRoute.sequenceStatus === "processed") {
        queryClient.invalidateQueries({
          queryKey: [
            "planning",
            sequencedRoute.id,
            "delivery_route",
            "trailer-shipments",
          ],
        });
      }
    });
  }, [
    isAutoSequenceFinished,
    processId,
    routeIdsKey,
    routeIds,
    sequenceSignature,
    sequenceStatus,
  ]);

  return {
    isAutoSequenceRunning: routeIds.length > 0,
  };
};
